import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FolderOpen, Plus, Loader2, Pencil } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { useOrgContext } from '@/providers/org-provider';

interface PortfolioRow {
  portfolioId: string;
  name: string;
  description: string | null;
  isDefault: boolean | null;
}

export function PortfoliosPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const { currentOrg } = useOrgContext();
  const queryClient = useQueryClient();

  // Create state
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['org', orgId, 'portfolios'],
    queryFn: () => apiClient.listPortfolios(orgId!),
    enabled: !!orgId,
  });
  const portfolios = (data?.data as PortfolioRow[] | undefined) ?? [];

  const create = useMutation({
    mutationFn: () => apiClient.createPortfolio(orgId!, { name: newName.trim(), description: newDesc.trim() }),
    onSuccess: () => {
      setNewName('');
      setNewDesc('');
      setCreating(false);
      queryClient.invalidateQueries({ queryKey: ['org', orgId, 'portfolios'] });
    },
  });

  const update = useMutation({
    mutationFn: ({ portfolioId }: { portfolioId: string }) =>
      apiClient.updatePortfolio(orgId!, portfolioId, { name: editName.trim(), description: editDesc.trim() }),
    onSuccess: () => {
      setEditingId(null);
      queryClient.invalidateQueries({ queryKey: ['org', orgId, 'portfolios'] });
    },
  });

  const isAdmin = currentOrg?.role === 'admin';

  const startEdit = (pf: PortfolioRow, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingId(pf.portfolioId);
    setEditName(pf.name);
    setEditDesc(pf.description ?? '');
  };

  const cancelEdit = () => setEditingId(null);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Portfolios</h1>
          <p className="text-sm text-muted-foreground">Group related projects under named portfolios.</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setCreating((s) => !s)}>
            <Plus className="h-4 w-4 mr-2" /> New portfolio
          </Button>
        )}
      </div>

      {creating && (
        <Card className="p-5 space-y-3">
          <Input
            placeholder="Portfolio name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            autoFocus
          />
          <Textarea
            placeholder="Description (optional)"
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            rows={2}
            className="resize-none"
          />
          <div className="flex gap-2">
            <Button disabled={!newName.trim() || create.isPending} onClick={() => create.mutate()}>
              {create.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Create
            </Button>
            <Button variant="outline" onClick={() => { setCreating(false); setNewName(''); setNewDesc(''); }}>Cancel</Button>
          </div>
        </Card>
      )}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {portfolios.map((pf) => {
            if (editingId === pf.portfolioId) {
              return (
                <Card key={pf.portfolioId} className="p-5 space-y-3">
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="Portfolio name"
                    autoFocus
                  />
                  <Textarea
                    value={editDesc}
                    onChange={(e) => setEditDesc(e.target.value)}
                    placeholder="Description (optional)"
                    rows={2}
                    className="resize-none"
                  />
                  <div className="flex gap-2">
                    <Button
                      disabled={!editName.trim() || update.isPending}
                      onClick={() => update.mutate({ portfolioId: pf.portfolioId })}
                    >
                      {update.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                      Save
                    </Button>
                    <Button variant="outline" onClick={cancelEdit}>Cancel</Button>
                  </div>
                </Card>
              );
            }

            return (
              <div key={pf.portfolioId} className="relative group">
                <Link to={`/orgs/${orgId}/portfolios/${pf.portfolioId}`} className="block">
                  <Card className="p-5 hover:bg-muted/40 transition-colors h-full">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="h-10 w-10 rounded-xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center shrink-0">
                          <FolderOpen className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-base font-semibold text-foreground truncate">{pf.name}</p>
                          {pf.description && (
                            <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">{pf.description}</p>
                          )}
                        </div>
                      </div>
                      {pf.isDefault && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-muted text-muted-foreground shrink-0">
                          Default
                        </span>
                      )}
                    </div>
                  </Card>
                </Link>
                {isAdmin && (
                  <button
                    type="button"
                    onClick={(e) => startEdit(pf, e)}
                    className="absolute top-3 right-3 p-1.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity bg-background border border-border hover:bg-muted"
                    aria-label="Edit portfolio"
                  >
                    <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
